import { Users, ClipboardList, CheckCircle, UserPlus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const GuestListPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="guestlist-page">
      <Seo title="Guest Lists — RSVP by Code" description="Land a spot on any Unight guest list. Enter your promoter's code, drop your details, and get your QR — no queue, no cover, just underground music." path="/guestlist" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 to-transparent" />

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <p className="text-zinc-500 text-lg italic mb-4 opacity-0 animate-fade-in"
             style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            For Promoters
          </p>

          <h1
            className="font-['Josefin_Sans'] text-5xl md:text-6xl lg:text-7xl font-bold italic text-white mb-6 opacity-0 animate-fade-in-up tracking-tight"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards', letterSpacing: '-0.059em' }}
            data-testid="guestlist-title"
          >
            GUEST LIST MANAGEMENT
          </h1>

          <p className="text-zinc-400 max-w-2xl opacity-0 animate-fade-in-up mb-8"
             style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            Streamline your guest list management. Create, share, and verify entries seamlessly. Built for promoters who own their nights.
          </p>

          <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up"
               style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            <Link to="/waitlist" className="btn-primary inline-flex items-center gap-2" data-testid="guestlist-become-promoter">
              BECOME A PROMOTER
              <ArrowRight size={16} />
            </Link>
            <Link to="/discovery" className="btn-secondary-dark inline-flex items-center gap-2">
              EXPLORE ARTISTS
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-16 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-12">
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">WHAT YOU GET</span>
            <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white">
              BUILT FOR THE FLOOR
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#B5FF00]/40 transition-colors">
              <UserPlus size={32} className="text-[#B5FF00] mb-4" />
              <h3 className="font-['Josefin_Sans'] text-xl text-white mb-2">Easy Entry</h3>
              <p className="text-zinc-500 text-sm">Add guests with name, phone, +1s, and KYC in seconds.</p>
            </div>

            <div className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#B5FF00]/40 transition-colors">
              <ClipboardList size={32} className="text-[#B5FF00] mb-4" />
              <h3 className="font-['Josefin_Sans'] text-xl text-white mb-2">Organised Lists</h3>
              <p className="text-zinc-500 text-sm">Alphabetically sorted lists for quick venue check-in.</p>
            </div>

            <div className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#B5FF00]/40 transition-colors">
              <Users size={32} className="text-[#B5FF00] mb-4" />
              <h3 className="font-['Josefin_Sans'] text-xl text-white mb-2">Team Collaboration</h3>
              <p className="text-zinc-500 text-sm">Multiple promoters can manage the same event.</p>
            </div>

            <div className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#B5FF00]/40 transition-colors">
              <CheckCircle size={32} className="text-[#B5FF00] mb-4" />
              <h3 className="font-['Josefin_Sans'] text-xl text-white mb-2">Verification</h3>
              <p className="text-zinc-500 text-sm">Age and entry requirements built-in.</p>
            </div>
          </div>

          {/* Pitch CTA */}
          <div className="bg-[#0A0A0A] border border-zinc-800 p-8 md:p-12 max-w-4xl mx-auto text-center">
            <h3 className="font-['Josefin_Sans'] text-3xl md:text-4xl text-white mb-4">
              Run a venue or night?
            </h3>
            <p className="text-zinc-400 max-w-xl mx-auto mb-8">
              Promoter accounts are granted by the Unight team. Drop your details on the waitlist with the “organiser” option and we’ll get back to you within 48 hours.
            </p>
            <Link to="/waitlist" className="btn-primary inline-flex items-center gap-2">
              REQUEST PROMOTER ACCESS
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2026 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GuestListPage;
